import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CalendarIcon, FileText, Activity } from 'lucide-react';
import { Client } from '@/types/client';
import { ClientQuestionnairesSection } from '@/clients/details/shared/components/ClientQuestionnairesSection';
import { DietaryIntakeGoals, DietaryIntakeGoalsRef } from './DietaryIntakeGoals';
import { MedicalConditionsSection } from './MedicalConditionsSection';
// Temporarily hidden: meal slot configuration on client details.
// import { MealTypesIntegrated } from '@/components/clients/meal-settings/MealTypesIntegrated';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import { ClientLabReportsSection } from '@/clients/details/shared/components/ClientLabReportsSection';

interface TabContentProps {
  activeTab: string;
  client: Client;
  refreshClientData?: () => Promise<void>;
}

export interface TabContentRef {
  saveDietaryGoals: () => Promise<any>;
  resetDietaryGoals: () => void;
}

export const TabContent = forwardRef<TabContentRef, TabContentProps>(({ 
  activeTab, 
  client, 
  refreshClientData = async () => {} 
}, ref) => {
  const dietaryGoalsRef = useRef<DietaryIntakeGoalsRef>(null);
  useImperativeHandle(ref, () => ({
    saveDietaryGoals: async () => {
      if (dietaryGoalsRef.current) {
        return await dietaryGoalsRef.current.saveDietaryGoals();
      }
    },
    resetDietaryGoals: () => {
      if (dietaryGoalsRef.current) {
        dietaryGoalsRef.current.resetToOriginalValues();
      }
    }
  }));

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return null; // This is handled separately in the main component

      case 'appointments':
        return (
          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-blue/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-foreground flex items-center">
                <div className="h-10 w-10 bg-blue/10 rounded-full flex items-center justify-center mr-3">
                  <CalendarIcon className="h-5 w-5 text-blue" />
                </div>
                Appointments
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground">Past and upcoming appointments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border/30">
                <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-base text-muted-foreground">Appointments will appear here.</p>
              </div>
            </CardContent>
          </Card>
        );

      case 'medical':
        return <MedicalConditionsSection client={client} />;

      case 'preferences':
        return (
          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-green/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-foreground flex items-center">
                <div className="h-10 w-10 bg-green/10 rounded-full flex items-center justify-center mr-3">
                  <Activity className="h-5 w-5 text-green" />
                </div>
                Food Preferences
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground">Dietary preferences and restrictions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border/30">
                <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-base text-muted-foreground">Food preferences will appear here.</p>
              </div>
            </CardContent>
          </Card>
        );

      case 'mealplans':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-end">
              <FeedbackButton
                clientId={client.id}
                label="Testing Feedback"
                defaultContext={FeedbackContext.CLIENT_DETAILS_DIETARY_GOALS}
                title="Testing Feedback – Dietary and Meal Plan Goals"
                description="Please provide feedback on the Dietary and Meal Plan Goals section. Please specify the date of feedback. Multiple feedback entries allowed."
                titlePlaceholder="e.g., Dietary goals configuration issues"
                detailsPlaceholder="Describe the steps, date of feedback, and observations when setting or reviewing dietary and meal plan goals."
                submitLabel="Save Client Feedback"
                successDescription="Thanks for helping us improve Dietary and Meal Plan Goals."
              />
            </div>
            <DietaryIntakeGoals
              ref={dietaryGoalsRef}
              clientId={client.id}
              client={client}
              refreshClientData={refreshClientData}
            />
            {/* Temporarily hidden: meal slot configuration on client details.
            <MealTypesIntegrated
              clientId={client.id}
              clientName={`${client.firstName} ${client.lastName}`}
              client={client}
              maxMealSlots={5}
              className="border-t pt-6"
            />
            */}
          </div>
        );

      case 'labReports':
        return (
          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-amber/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-foreground flex items-center">
                <div className="h-10 w-10 bg-amber/10 rounded-full flex items-center justify-center mr-3">
                  <Activity className="h-5 w-5 text-amber" />
                </div>
                Lab Reports
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground">Client's laboratory test results</CardDescription>
            </CardHeader>
            <CardContent>
              <ClientLabReportsSection
                clientId={client.id}
                clientName={[client.firstName, client.lastName].filter(Boolean).join(' ').trim()}
                onRefresh={refreshClientData}
              />
            </CardContent>
          </Card>
        );

      case 'questionnaires':
        return (
          <ClientQuestionnairesSection clientId={client.id} />
        );

      default:
        return null;
    }
  };

  return <div className="mt-0">{renderTabContent()}</div>;
});

TabContent.displayName = 'TabContent';
